import { useState, useMemo } from 'react';
import {
  Button,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { InfoIcon, PencilIcon } from 'lucide-react';
import { ProviderWhitelistEditForm } from '../ProviderWhitelistEditForm';
import { RemoveProviderConfirmationModal } from '../RemoveProviderConfirmationModal';
import { useSaveProviderWhitelist } from '../../model';

import type {
  ModelProviderResponseDto,
  ModelProviderWhitelistResponseDto,
} from '@inference.engine/api-client/web';
import { useTranslation } from 'react-i18next';

interface ProviderWhitelistCardProps {
  whitelistedProviders: ModelProviderWhitelistResponseDto[];
  allProviders: ModelProviderResponseDto[];
}

/**
 * Renders the card component for viewing and updating organization level provider whitelists.
 */
export const ProviderWhitelistCard = ({
  whitelistedProviders,
  allProviders,
}: ProviderWhitelistCardProps) => {
  const { t } = useTranslation('admin');

  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);

  // Derive org-level providers (those without workspaceId)
  const orgLevelProviders = useMemo(() => {
    return whitelistedProviders.filter((p) => !p.workspaceId);
  }, [whitelistedProviders]);

  const {
    showConfirmation,
    affectedProviders,
    isProcessing,
    handleSave,
    handleConfirmRemove,
    handleCancelConfirmation,
  } = useSaveProviderWhitelist({
    orgLevelProviders,
    selectedProviders,
    allWhitelistedProviders: whitelistedProviders,
    onSaveSuccess: () => setIsEditMode(false),
  });

  const handleEditClick = () => {
    setSelectedProviders(orgLevelProviders.map((p) => p.key));
    setIsEditMode(true);
  };

  const handleCancelClick = () => {
    setSelectedProviders([]);
    setIsEditMode(false);
  };

  return (
    <>
      <div className="border border-border flex flex-col gap-4 p-6 rounded-2xl">
        <div className="flex items-center justify-between">
          <div className="flex gap-1 items-center">
            <h2 className="text-base font-medium text-foreground leading-6">
              {t('admin.providerWhitelistCard.aiModelProviders')}
            </h2>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <InfoIcon className="size-5 text-muted-foreground" />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {t('admin.providerWhitelistCard.configureWhichAiModelProviders')}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          {isEditMode ? (
            <div className="flex gap-2 items-center">
              <Button type="button" variant="outline" onClick={handleCancelClick}>
                {t('admin.providerWhitelistCard.cancel')}
              </Button>
              <Button
                type="button"
                onClick={() => void handleSave()}
                disabled={isProcessing || selectedProviders.length === 0}
              >
                {/* Hidden from the a11y tree so the button keeps its own name; the
                    disabled state is what announces that the save is in flight. */}
                {isProcessing && <Spinner aria-hidden="true" />}
                {t('admin.providerWhitelistCard.save')}
              </Button>
            </div>
          ) : (
            <Button type="button" variant="outline" onClick={handleEditClick}>
              {t('admin.providerWhitelistCard.edit')}
              <PencilIcon className="text-muted-foreground" />
            </Button>
          )}
        </div>
        {isEditMode ? (
          <ProviderWhitelistEditForm
            allProviders={allProviders}
            selectedProviders={selectedProviders}
            onToggleProvider={(providerKey: string) => {
              setSelectedProviders((prev) =>
                prev.includes(providerKey)
                  ? prev.filter((p) => p !== providerKey)
                  : [...prev, providerKey],
              );
            }}
            isPending={isProcessing}
          />
        ) : (
          <div className="flex gap-1 items-center flex-wrap">
            <span className="text-base text-muted-foreground leading-6">
              {orgLevelProviders.length > 0
                ? orgLevelProviders.map((p) => p.name).join('  •  ')
                : t('admin.providerWhitelistCard.noProvidersSelected')}
            </span>
          </div>
        )}
      </div>

      <RemoveProviderConfirmationModal
        open={showConfirmation}
        isProcessing={isProcessing}
        onCancel={handleCancelConfirmation}
        onConfirm={handleConfirmRemove}
        affectedProviders={affectedProviders}
      />
    </>
  );
};
